import type { ImportColumn, ImportField } from '@paintscout/api'
import { Alert, makeStyles, Select, Tooltip, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import WarningIcon from '@material-ui/icons/Warning'
import React from 'react'
import { getCombinedFieldBannerMessage, getCombinedFieldTooltip } from '../combinedFieldHint'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  // Reads as a quiet caption under the stepper, not as body copy. `em`, not `rem`, so it still
  // follows the theme's fontScale — body2 is deliberately the same size as body1 in this theme.
  summary: {
    fontSize: '0.8125em',
    color: theme.paintscout.palette.brand.Neutral.P60
  },
  // Rows run full-bleed like the heading band above them, so their dividers reach both dialog
  // edges. No gap between rows — the dividers do the separating.
  rows: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(0, -2),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(0, -3)
    }
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5, 3)
    }
  },
  source: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0
  },
  sourceHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  sample: {
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  arrow: {
    color: theme.palette.primary.main,
    fontSize: 18
  },
  warningIcon: {
    color: theme.palette.warning.main,
    fontSize: 18
  },
  columnLabel: {
    color: theme.palette.text.secondary
  },
  // A tinted band with a rule above and below, running the full width of the dialog body — hence
  // the negative side margins, which cancel DialogContent's own padding and are re-applied as
  // padding so the two headings still line up with the rows beneath them.
  headings: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    gap: theme.spacing(2),
    backgroundColor: theme.paintscout.palette.brand.Neutral.P98,
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    // The trailing -2 cancels `root`'s flex gap so the rows start flush against the band.
    margin: theme.spacing(0, -2, -2),
    padding: theme.spacing(1.5, 2),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(0, -3, -2),
      padding: theme.spacing(1.5, 3)
    }
  },
  // The source column name is the row's heading; its sample value sits under it as support text.
  sourceLabel: {
    fontWeight: 600
  },
  // The shared Select's `outlined` variant draws a near-black `text.primary` border (#24959), and
  // MUI's InputBase pins the native <select> to `height: 1.1876em`, which clips descenders such
  // as the "g" in "Tags" (#24955). Restyle it to match the app's Input / DropdownSelect fields.
  // This class goes on the <select> itself via `inputProps.className`. Do not select it through a
  // MUI global class (`.MuiSelect-select`): the app nests a second ThemeProvider (AuthorizeUser's
  // <Theme>), and under a nested theme MUI suffixes those names (`MuiSelect-select-123`), so such a
  // selector matches nothing in the real app (#24959 reopened). `&&&` (0,3,0) out-ranks the shared
  // Select's `outlined` + `select` pair (0,2,0); the focus rule (0,4,0) also beats its
  // `:focus-within` ring and MUI's grey `.MuiSelect-select:focus` fill.
  fieldSelect: {
    '&&&': {
      height: 'auto',
      // A real native <select>: below `sm`, body1 shrinks under 16px and iPad Safari zooms on
      // focus with no way back out (`user-scalable=no`). Same floor as the shared Input.
      fontSize: 'max(16px, 1rem)',
      borderColor: theme.paintscout.palette.brand.Neutral.P85,
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.palette.background.paper
    },
    '&&&:hover:not(:focus)': {
      borderColor: 'transparent',
      boxShadow: `0 0 0 2px ${theme.paintscout.palette.brand.Neutral.P70}`
    },
    // MUI greys a focused native select's background; the app's fields keep it white.
    '&&&:focus, &&&:focus-within, &&&:active': {
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.borderRadius.md,
      borderColor: 'transparent',
      boxShadow: `0 0 0 2px ${theme.paintscout.palette.primary}`
    }
  }
}))

export const DONT_IMPORT_LABEL = "Don't import"
export const DONT_IMPORT_VALUE = ''

export interface MapFieldsStepProps {
  fileName: string
  rowCount: number
  matchedCount: number
  columns: ImportColumn[]
  fields: ImportField[]
  mapping: Record<number, string | null>
  usedFieldKeys: Set<string>
  missingRequiredFields: ImportField[]
  combinedFieldColumns: number[]
  entityNoun: string
  onChangeMapping: (columnIndex: number, field: string | null) => void
}

/**
 * Step 2: match each column in the uploaded file to a PaintScout field.
 *
 * One row per source column — the list is dynamic per upload, never the full field list. The
 * mapping stays one-to-one: a field already taken elsewhere is disabled here.
 */
export default function MapFieldsStep({
  fileName,
  rowCount,
  matchedCount,
  columns,
  fields,
  mapping,
  usedFieldKeys,
  missingRequiredFields,
  combinedFieldColumns,
  entityNoun,
  onChangeMapping
}: MapFieldsStepProps) {
  const classes = useStyles()
  const flagged = new Set(combinedFieldColumns)

  return (
    <div className={classes.root}>
      <Typography variant="body2" className={classes.summary} data-testid="map-fields-summary">
        Matched <strong>{matchedCount}</strong> of <strong>{columns.length}</strong> columns from{' '}
        <strong>{fileName}</strong> ({rowCount} {rowCount === 1 ? 'row' : 'rows'}). Adjust any mapping below.
      </Typography>

      {flagged.size > 0 && (
        <Alert severity="warning" noMargin>
          {getCombinedFieldBannerMessage(flagged.size)}
        </Alert>
      )}

      {missingRequiredFields.length > 0 && (
        <Alert severity="error" noMargin>
          Map a column to: {missingRequiredFields.map((field) => field.label).join(', ')}
        </Alert>
      )}

      <div className={classes.headings}>
        <Typography variant="body2" className={classes.columnLabel}>
          Column from your file
        </Typography>
        <span />
        <Typography variant="body2" className={classes.columnLabel}>
          {entityNoun} field
        </Typography>
      </div>

      <div className={classes.rows}>
        {columns.map((column) => {
          const selected = mapping[column.index] ?? DONT_IMPORT_VALUE
          const isFlagged = flagged.has(column.index)

          return (
            <div key={column.index} className={classes.row} data-testid={`map-row-${column.index}`}>
              <div className={classes.source}>
                <div className={classes.sourceHeader}>
                  {isFlagged && (
                    // The icon is focusable so keyboard users can open the tooltip with the fix.
                    <Tooltip title={getCombinedFieldTooltip(mapping[column.index])} hideIcon>
                      <WarningIcon
                        className={classes.warningIcon}
                        aria-label="combined field warning"
                        role="img"
                        tabIndex={0}
                      />
                    </Tooltip>
                  )}
                  <Typography variant="body1" className={classes.sourceLabel}>
                    {column.header}
                  </Typography>
                </div>
                {column.sample && (
                  <Typography variant="body2" className={classes.sample}>
                    e.g. {column.sample}
                  </Typography>
                )}
              </div>

              <ArrowForwardIcon className={classes.arrow} />

              <Select
                native
                variant="outlined"
                value={selected}
                inputProps={{ 'aria-label': `Field for ${column.header}`, className: classes.fieldSelect }}
                onChange={(event) => {
                  const value = event.target.value as string
                  onChangeMapping(column.index, value === DONT_IMPORT_VALUE ? null : value)
                }}
              >
                <option value={DONT_IMPORT_VALUE}>{DONT_IMPORT_LABEL}</option>
                {fields.map((field) => {
                  const takenElsewhere = usedFieldKeys.has(field.key) && mapping[column.index] !== field.key

                  return (
                    <option key={field.key} value={field.key} disabled={takenElsewhere}>
                      {field.label}
                      {field.required ? ' *' : ''}
                      {takenElsewhere ? ' (mapped)' : ''}
                    </option>
                  )
                })}
              </Select>
            </div>
          )
        })}
      </div>
    </div>
  )
}
